import { parseDecimalInput } from '../../shared/numeric/localeNumeric';
import type { CustomerAccountRow } from '../domain/customerTypes';

import { formatMoney } from '../../shared/format/public-api';
export type CustomerManualMovementType = 'charge' | 'payment';

export type CustomerSalePendingOption = {
  id: string;
  shortId?: string;
  pendingAmount: number;
  total?: number;
};

const parseAmount = (raw: string) => parseDecimalInput(raw, { allowNegative: true });

export type CustomerMovementFieldError = {
  title: string;
  message: string;
};

export function getCustomerAmountFieldError(amountRaw: string): CustomerMovementFieldError | null {
  const trimmed = amountRaw.trim();
  if (!trimmed) {
    return {
      title: 'Monto requerido',
      message: 'Ingresá un importe.',
    };
  }
  const amount = parseAmount(amountRaw);
  if (amount === null || amount <= 0) {
    return {
      title: 'Monto inválido',
      message: 'Usá un número mayor a cero (ej. 1500 o 1500,50).',
    };
  }
  return null;
}

export function isCustomerAmountReady(amountRaw: string): boolean {
  const amount = parseAmount(amountRaw);
  return amount !== null && amount > 0;
}

export function getCustomerChargeBlockedError(customer: Pick<CustomerAccountRow, 'blocked'>): CustomerMovementFieldError | null {
  if (!customer.blocked) return null;
  return {
    title: 'Cliente bloqueado',
    message: 'No podés registrar consumos hasta desbloquear al cliente.',
  };
}

/** Cheque requiere número (el server lo rechaza sin él: customer_check_number_required). */
export function getCustomerCheckNumberError(checkNumberRaw: string): CustomerMovementFieldError | null {
  if (checkNumberRaw.trim()) return null;
  return {
    title: 'Número de cheque',
    message: 'Ingresá el número del cheque.',
  };
}

export function getCustomerPaymentNoDebtHint(balance: number): CustomerMovementFieldError | null {
  if (balance > 0) return null;
  return {
    title: 'Sin deuda',
    message: 'Este cliente no debe nada. No hace falta cobrar.',
  };
}

/**
 * Si el cargo supera el límite (>0), requiere autorización de supervisor (PIN).
 * El backend es la autoridad (ADR-027 / apply_customer_command).
 */
export function getCustomerCreditLimitConfirmation(
  customer: Pick<CustomerAccountRow, 'balance' | 'creditLimit'>,
  chargeAmount: number
): CustomerMovementFieldError | null {
  const limit = Number(customer.creditLimit ?? 0);
  if (limit <= 0) return null;
  const nextBalance = Number(customer.balance ?? 0) + chargeAmount;
  if (nextBalance <= limit) return null;
  return {
    title: 'Se pasa del límite',
    message: `Saldo actual: ${formatMoney(Number(customer.balance ?? 0))}\nConsumo: ${formatMoney(chargeAmount)}\nSaldo estimado: ${formatMoney(nextBalance)}\nLímite: ${formatMoney(limit)}\n\nPara cargar igual, necesitás autorización de supervisor.`,
  };
}

/** Tope de cobro: pendiente de la venta elegida, o saldo total del cliente. */
export function getCustomerPaymentAmountError(
  amountRaw: string,
  balance: number,
  linkedSale?: CustomerSalePendingOption | null
): CustomerMovementFieldError | null {
  const amount = parseAmount(amountRaw);
  if (amount === null || amount <= 0) return null;

  if (linkedSale) {
    const pending = Number(linkedSale.pendingAmount ?? 0);
    if (amount <= pending) return null;
    const saleRef = linkedSale.shortId ? `#${linkedSale.shortId}` : `#${linkedSale.id.slice(-6)}`;
    return {
      title: 'Monto mayor al pendiente',
      message: `La venta ${saleRef} tiene ${formatMoney(pending)} pendientes. El cobro no puede superar eso.`,
    };
  }

  const debt = Math.max(0, Number(balance ?? 0));
  if (amount <= debt) return null;
  return {
    title: 'Monto mayor a la deuda',
    message: `Debe ${formatMoney(debt)}. El cobro no puede superar eso.`,
  };
}

export function formatCustomerSelectedSaleLabel(sale: CustomerSalePendingOption): string {
  const saleRef = sale.shortId ? `#${sale.shortId}` : `#${sale.id.slice(-6)}`;
  const pending = formatMoney(Number(sale.pendingAmount ?? 0));
  const total = Number(sale.total ?? 0);
  if (total > 0) {
    return `Venta ${saleRef} · ${pending} pendiente de ${formatMoney(total)}`;
  }
  return `Venta ${saleRef} · ${pending} pendiente`;
}

export type CustomerMixedDebtBreakdown = {
  balance: number;
  pendingSalesTotal: number;
  generalDebt: number;
  pendingSaleCount: number;
};

/** Desglosa saldo entre ventas POS pendientes y deuda general (consumos manuales, etc.). */
export function getCustomerMixedDebtBreakdown(
  balance: number,
  pendingSales: ReadonlyArray<{ pendingAmount: number }>
): CustomerMixedDebtBreakdown {
  const pendingSalesTotal = pendingSales.reduce(
    (sum, sale) => sum + Math.max(0, Number(sale.pendingAmount ?? 0)),
    0
  );
  const normalizedBalance = Math.max(0, Number(balance ?? 0));
  return {
    balance: normalizedBalance,
    pendingSalesTotal,
    generalDebt: Math.max(0, normalizedBalance - pendingSalesTotal),
    pendingSaleCount: pendingSales.length,
  };
}



export type CustomerMixedDebtHint = {
  title: string;
  lines: string[];
};

/** Texto cuando hay consumo manual + ventas POS pendientes. */
export function getCustomerMixedDebtPaymentHint(breakdown: CustomerMixedDebtBreakdown): CustomerMixedDebtHint | null {
  if (!(breakdown.pendingSaleCount > 0 && breakdown.generalDebt > 0)) return null;

  const { balance, generalDebt, pendingSalesTotal, pendingSaleCount } = breakdown;
  const ventasLabel =
    pendingSaleCount === 1 ? '1 venta pendiente' : `${pendingSaleCount} ventas pendientes`;

  return {
    title: 'Ventas y consumos',
    lines: [
      `Debe ${formatMoney(balance)}: ${formatMoney(pendingSalesTotal)} en ${ventasLabel} y ${formatMoney(generalDebt)} en consumos manuales.`,
      'Si no elegís una venta, el cobro baja primero las ventas más antiguas. Lo que sobre baja los consumos manuales.',
    ],
  };
}
